import type { Dispatch } from 'react';
import { useEffect, useRef } from 'react';
import type { BrandInsightsInstanceContext, TagFilter } from '@trinity/chat';
import { extractBrandInsightsIds, getPatientJourneyIdFromChatContext, getSegmentFromChatContext } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { ChatState } from './interfaces';

type Params = {
  /**
   * The chat's own context (`chatInstance.context`), not the page's: it starts as the page's and is
   * replaced whenever the page changes, and it also follows the Segment / Patient Journey filters, so
   * a selection made there is mirrored into Tags (and a cleared one removed) the same way.
   */
  context: BrandInsightsInstanceContext.ItemWithMetadata | undefined;
  chatIdentifier: string;
  tags: TagFilter.Item[] | undefined;
  dispatch: Dispatch<ChatState.Action>;
};

const isSegmentTag = (tag: TagFilter.Item) => !!tag.metadata?.segmentIdentifier;
const isJourneyTag = (tag: TagFilter.Item) => typeof tag.metadata?.journeyId === 'number';

/**
 * A segment in the chat context (`metadata.segment`) is mirrored into the Tags filter as a locked
 * (non-removable) segment tag, the same shape `SegmentChatSelection` applies when a segment is picked
 * inside the chat. Re-applies when the segment or the chat changes (a new chat starts with empty
 * filters). Only one segment scopes a chat, so every other segment's tag goes when this one is
 * applied, whether this hook applied it or the user picked it in Tags.
 */
export function useSegmentTagFilterSync({ context, chatIdentifier, tags, dispatch }: Params) {
  const apiClient = useChatApiClient();
  const segment = context ? getSegmentFromChatContext(context) : undefined;
  const segmentId = segment?.id;
  const segmentName = segment?.name;
  const hasContext = !!context;
  const projectId = context ? extractBrandInsightsIds(context).projectId : undefined;

  // Read through refs so a new context object with the same segment (every query response brings
  // one) or applying a tag does not retrigger the effects below.
  const contextRef = useRef(context);
  // Declared ahead of the sync effects below, so it holds the latest context by the time they run.
  useEffect(() => {
    contextRef.current = context;
  });
  const tagsRef = useRef(tags);
  tagsRef.current = tags;
  const appliedTagIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const currentContext = contextRef.current;

    if (!currentContext || !segmentId || !segmentName) {
      const appliedTagId = appliedTagIdRef.current;
      if (appliedTagId !== null) {
        appliedTagIdRef.current = null;
        dispatch({
          type: 'filters/updated',
          payload: { filters: { tags: (tagsRef.current ?? []).filter((t) => t.id !== appliedTagId) } },
        });
      }
      return undefined;
    }

    // Drop the other segments' tags before looking this one up, so a failed or empty lookup can't leave
    // the previous segment filtering the evidence while the chat is scoped to this one.
    const stale = (tagsRef.current ?? []).filter(
      (t) => isSegmentTag(t) && t.metadata?.segmentIdentifier !== segmentId
    );
    if (stale.length) {
      if (stale.some((t) => t.id === appliedTagIdRef.current)) appliedTagIdRef.current = null;
      dispatch({
        type: 'filters/updated',
        payload: { filters: { tags: (tagsRef.current ?? []).filter((t) => !stale.includes(t)) } },
      });
    }

    apiClient.chat
      .searchTags({ context: currentContext, query: segmentName })
      .then((res) => {
        if (cancelled) return;

        // The segment's group tag is the one linked to this segment identifier.
        const match = res.data.results.find((t) => t.metadata?.segmentIdentifier === segmentId);
        if (!match) return;

        appliedTagIdRef.current = match.id;

        const others = (tagsRef.current ?? []).filter((t) => t.id !== match.id && !isSegmentTag(t));
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags: [
                ...others,
                {
                  id: match.id,
                  name: match.name,
                  metadata: { canRemove: false, segmentIdentifier: segmentId },
                },
              ],
            },
          },
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // `tags` and `context` are read through refs so applying the tag does not retrigger this effect.
  }, [apiClient, chatIdentifier, dispatch, hasContext, projectId, segmentId, segmentName]);

  // Same for a Patient Journey: `metadata.patientJourneyId` on the chat context is shown as a locked
  // entry in Tags (journeys come back from the tag search for a project context, under a synthetic
  // negative id with `metadata.journeyId`). One journey at a time, like the segment.
  const journeyId = context ? getPatientJourneyIdFromChatContext(context) : undefined;
  const appliedJourneyTagIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const currentContext = contextRef.current;

    if (!currentContext || journeyId === undefined) {
      const appliedTagId = appliedJourneyTagIdRef.current;
      if (appliedTagId !== null) {
        appliedJourneyTagIdRef.current = null;
        dispatch({
          type: 'filters/updated',
          payload: { filters: { tags: (tagsRef.current ?? []).filter((t) => t.id !== appliedTagId) } },
        });
      }
      return undefined;
    }

    const stale = (tagsRef.current ?? []).filter(
      (t) => isJourneyTag(t) && t.metadata?.journeyId !== journeyId
    );
    if (stale.length) {
      if (stale.some((t) => t.id === appliedJourneyTagIdRef.current)) appliedJourneyTagIdRef.current = null;
      dispatch({
        type: 'filters/updated',
        payload: { filters: { tags: (tagsRef.current ?? []).filter((t) => !stale.includes(t)) } },
      });
    }

    apiClient.chat
      .searchTags({ context: currentContext, query: '' })
      .then((res) => {
        if (cancelled) return;

        const match = res.data.results.find((t) => t.metadata?.journeyId === journeyId);
        if (!match) return;

        appliedJourneyTagIdRef.current = match.id;

        const others = (tagsRef.current ?? []).filter((t) => t.id !== match.id && !isJourneyTag(t));
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags: [
                ...others,
                { id: match.id, name: match.name, metadata: { canRemove: false, journeyId } },
              ],
            },
          },
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // `tags` and `context` are read through refs so applying the tag does not retrigger this effect.
  }, [apiClient, chatIdentifier, dispatch, hasContext, projectId, journeyId]);
}
